import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Sun, Moon, Menu, LogOut, LogIn, User as UserIcon, LayoutDashboard, KeyRound } from "lucide-react";
import { useTheme } from "@/theme/ThemeProvider";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, ESCUDO } from "@/lib/api";
import { toast } from "sonner";
import { useState } from "react";
import NotificationsBell from "@/components/NotificationsBell";

const publicNav = [
  { to: "/", label: "Inicio" },
  { to: "/centenario", label: "Centenario" },
  { to: "/oracion", label: "Rinconcito de oración" },
  { to: "/eucaristia", label: "Eucaristía" },
];

const memberNav = [
  { to: "/primera-comunion", label: "Primera Comunión" },
  { to: "/confirmacion", label: "Confirmación" },
];

export default function Header() {
  const { theme, toggle } = useTheme();
  const { user, logout, refresh } = useAuth();
  const navigate = useNavigate();
  const [pwdOpen, setPwdOpen] = useState(false);

  const dashboardPath = user && (user.role === "catechist" || user.role === "admin") ? "/dashboard/catequista" : "/dashboard/estudiante";

  return (
    <header className="sticky top-0 z-50 glass-header border-b border-border" data-testid="site-header">
      <div className="container mx-auto px-4 sm:px-6 lg:px-10 h-20 flex items-center justify-between gap-4">
        {/* Escudo as Home button */}
        <Link to="/" className="flex items-center gap-3 group shrink-0" data-testid="escudo-home-btn" aria-label="Inicio">
          <span className="relative inline-flex w-14 h-14 rounded-full bg-white border-2 border-primary p-1 shadow-md group-hover:shadow-xl group-hover:scale-105 transition-all duration-300 overflow-hidden">
            <img src={ESCUDO} alt="Escudo Colegio Franciscano Virrey Solís" className="w-full h-full object-contain rounded-full" />
          </span>
          <div className="hidden sm:flex flex-col leading-tight">
            <span className="font-display text-xl text-primary font-bold">Pastoral Virreyista</span>
            <span className="text-[10px] tracking-[0.25em] uppercase text-secondary font-bold">Virtud & Ciencia · 2026</span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-6" data-testid="main-nav">
          {publicNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `text-sm tracking-wide hover:text-primary transition-colors ${
                  isActive ? "text-primary font-semibold" : "text-foreground/75"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          {user && user.user_id && memberNav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.to.replace("/", "")}`}
              className={({ isActive }) =>
                `text-sm tracking-wide hover:text-primary transition-colors ${
                  isActive ? "text-primary font-semibold" : "text-foreground/75"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={toggle}
            aria-label="Cambiar tema"
            data-testid="theme-toggle"
            className="rounded-full"
          >
            {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
          </Button>

          {user && user.user_id ? (
            <>
              <NotificationsBell />
              <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="rounded-full pl-2 pr-4 gap-2" data-testid="user-menu-btn">
                  <span className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-bold">
                    {user.name?.[0]?.toUpperCase() || "U"}
                  </span>
                  <span className="hidden sm:inline text-sm">{user.name?.split(" ")[0]}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="text-xs">
                  <div className="font-semibold">{user.name}</div>
                  <div className="text-foreground/60">{user.email}</div>
                  <div className="text-secondary font-bold uppercase tracking-wider text-[10px] mt-1">
                    {user.role === "admin" ? "Administrador" : user.role === "catechist" ? "Catequista" : "Estudiante"}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate(dashboardPath)} data-testid="menu-dashboard">
                  <LayoutDashboard className="w-4 h-4 mr-2" /> Mi panel
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setPwdOpen(true)} data-testid="menu-change-password">
                  <KeyRound className="w-4 h-4 mr-2" /> Cambiar contraseña
                </DropdownMenuItem>
                <DropdownMenuItem onClick={async () => { await logout(); navigate("/"); }} data-testid="menu-logout">
                  <LogOut className="w-4 h-4 mr-2" /> Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            </>
          ) : (
            <Button asChild className="rounded-full" data-testid="login-cta">
              <Link to="/login"><LogIn className="w-4 h-4 mr-1" /> Ingresar</Link>
            </Button>
          )}

          <Sheet>
            <SheetTrigger asChild className="lg:hidden">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <div className="flex flex-col gap-1 mt-8" data-testid="mobile-nav">
                {publicNav.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    data-testid={`mobile-nav-${item.to.replace("/", "") || "home"}`}
                    className={({ isActive }) =>
                      `px-4 py-3 rounded-lg text-base transition-colors ${
                        isActive ? "bg-primary text-primary-foreground" : "hover:bg-muted"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
                {user && user.user_id && memberNav.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className="px-4 py-3 rounded-lg text-base hover:bg-muted"
                  >
                    {item.label}
                  </NavLink>
                ))}
                {user && user.user_id && (
                  <NavLink to={dashboardPath} className="px-4 py-3 rounded-lg text-base hover:bg-muted">Mi Panel</NavLink>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      <ChangePasswordDialog open={pwdOpen} onClose={() => setPwdOpen(false)} onChanged={refresh} mustChange={user?.must_change_password} />
      {user?.user_id && user?.must_change_password && (
        <div className="bg-secondary text-secondary-foreground text-xs sm:text-sm py-2 px-4 text-center font-semibold" data-testid="must-change-banner">
          🔐 Por seguridad, te recomendamos cambiar tu contraseña temporal.{" "}
          <button onClick={() => setPwdOpen(true)} className="underline ml-1" data-testid="must-change-btn">Cambiarla ahora →</button>
        </div>
      )}
    </header>
  );
}

function ChangePasswordDialog({ open, onClose, onChanged, mustChange }) {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.next.length < 6) return toast.error("Mínimo 6 caracteres");
    if (form.next !== form.confirm) return toast.error("Las contraseñas no coinciden");
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: form.current, new_password: form.next });
      toast.success("Contraseña actualizada");
      setForm({ current: "", next: "", confirm: "" });
      if (onChanged) await onChanged();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo cambiar");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-primary">Cambiar contraseña</DialogTitle>
        </DialogHeader>
        {mustChange && (
          <p className="text-xs text-secondary bg-secondary/10 border border-secondary/30 rounded-lg px-3 py-2">
            Estás usando una contraseña temporal. Defínela ahora para mayor seguridad.
          </p>
        )}
        <form onSubmit={submit} className="space-y-3" data-testid="change-pwd-form">
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Contraseña actual</Label>
            <Input type="password" required value={form.current} onChange={(e) => setForm({ ...form, current: e.target.value })} className="mt-1.5 h-11" data-testid="cp-current" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Nueva contraseña</Label>
            <Input type="password" required minLength={6} value={form.next} onChange={(e) => setForm({ ...form, next: e.target.value })} className="mt-1.5 h-11" data-testid="cp-next" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider font-semibold">Confirmar nueva contraseña</Label>
            <Input type="password" required minLength={6} value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} className="mt-1.5 h-11" data-testid="cp-confirm" />
          </div>
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-full">Cancelar</Button>
            <Button type="submit" disabled={busy} className="rounded-full" data-testid="cp-submit">{busy ? "Guardando…" : "Guardar"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
